import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  AveragePlayerScore,
  ScoreStatus,
} from '#src/__generated__/globalTypes.ts';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { Score } from '#src/components/scoring/Score/index.tsx';
import type { TotalAveragePlayerScore } from '#src/lib/averagePlayerScore.ts';
import { statusMessages } from '#src/lib/players.ts';
import { modeTooltipMessages } from '#src/lib/so5.ts';

type Props = {
  scores: Record<TotalAveragePlayerScore, number | null>;
  mode: TotalAveragePlayerScore;
  remainingPoints?: number;
  hideTooltip?: boolean;
  small?: boolean;
} & Pick<
  ComponentProps<typeof Score>,
  'animated' | 'isCappedMode' | 'shape' | 'size'
>;

const INVERTED: TotalAveragePlayerScore[] = [
  AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
  AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
];
const NO_BORDER: TotalAveragePlayerScore[] = [
  AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
];

export const ScoreByMode = ({
  scores,
  mode,
  isCappedMode,
  remainingPoints,
  animated,
  hideTooltip,
  shape = 'HEXAGON',
  size,
}: Props) => {
  const score = scores[mode];

  const isAffordable =
    remainingPoints !== undefined ? remainingPoints >= (score || 0) : true;

  return (
    <Tooltip
      title={
        <FormattedMessage
          {...(score ? modeTooltipMessages[mode] : statusMessages.DID_NOT_PLAY)}
        />
      }
      skip={hideTooltip}
    >
      <Score
        hideReviewing
        shape={shape}
        score={score || 0}
        scoreStatus={ScoreStatus.FINAL}
        scoreType="PLAYER_SCORE"
        isCappedMode={isCappedMode}
        isAffordable={isAffordable}
        animated={animated}
        inverted={INVERTED.includes(mode)}
        noBorder={NO_BORDER.includes(mode)}
        size={size}
      />
    </Tooltip>
  );
};
